import type { QuestionCheckboxModel, ItemValue } from "survey-core";
import { Check } from "lucide-react";
import { cn } from "@rumbledirect/ui-v2/cn";
import { useSurveyQuestionRerender } from "./useSurveyQuestionRerender";

/**
 * Renderer for SurveyJS `checkbox` questions. There is deliberately no
 * `Checkbox` in admin-v2's `components/ui` barrel — every boolean choice in
 * the app so far uses the `Switch` treatment, but a SurveyJS checkbox
 * question is a *multi-select list* (the model value is an array of choice
 * values), which `Switch` cannot express. So this renderer gives each choice
 * the admin-v2 control treatment (same border/radius/focus/hover language as
 * `Input` and `Select`) with a check icon for the selected state.
 *
 * `selectAll` / `none` / `other` special items are rendered as-is among the
 * regular choices; the seeded schemas do not use them. Toggling goes through
 * the model so `visibleIf` and validation react like they do for any other
 * question type.
 */
export function SurveyCheckboxQuestion({
  question,
}: {
  readonly question: QuestionCheckboxModel;
}) {
  useSurveyQuestionRerender(question);
  const selected = new Set(
    (Array.isArray(question.value) ? question.value : []).map(String),
  );

  const toggle = (choice: ItemValue) => {
    if (question.isDisabledAttr || question.isReadOnlyAttr) return;
    // Read the model's current value at event time rather than the render's
    // `selected` snapshot: multiple toggles can fire between re-renders.
    const current = new Set(
      (Array.isArray(question.value) ? question.value : []).map(String),
    );
    const next = new Set(current);
    if (next.has(String(choice.value))) {
      next.delete(String(choice.value));
    } else {
      next.add(String(choice.value));
    }
    question.value = [...next];
  };

  return (
    // There is no per-choice description or error element, so each checkbox
    // references the shared group ids rendered by SurveyQuestionChrome — the
    // SurveyJS a11y contract for multi-choice controls.
    <div className="flex flex-col gap-2">
      {question.visibleChoices.map((choice) => {
        const isChecked = selected.has(String(choice.value));
        return (
          <label
            key={String(choice.value)}
            className={cn(
              "flex cursor-pointer items-center gap-3 rounded-xl border border-input bg-surface px-3 py-2.5 text-sm text-foreground shadow-sm transition-colors hover:bg-muted/50 has-focus-visible:border-primary has-focus-visible:ring-2 has-focus-visible:ring-primary/15 has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50",
              isChecked && "border-primary/50 bg-primary-soft",
            )}
          >
            <input
              type="checkbox"
              className="peer sr-only"
              checked={isChecked}
              // `readonly` is ignored on checkbox inputs by the HTML spec,
              // so a read-only question must disable the control instead —
              // the same fallback the dropdown/date-picker renderers use.
              disabled={question.isDisabledAttr || question.isReadOnlyAttr}
              onChange={() => toggle(choice)}
              aria-invalid={question.a11y_input_ariaInvalid}
              aria-describedby={
                question.a11y_input_ariaDescribedBy ?? undefined
              }
            />
            <span
              aria-hidden="true"
              className={cn(
                "flex size-5 shrink-0 items-center justify-center rounded-md border border-input bg-surface text-transparent transition-colors",
                isChecked &&
                  "border-primary bg-primary text-primary-foreground",
              )}
            >
              <Check className="size-3.5" strokeWidth={3} />
            </span>
            {choice.text}
          </label>
        );
      })}
    </div>
  );
}
